import {
  BulkAssignmentImportResponse,
  PayCodeModel,
  PayRateTypeModel,
  ValidationFailure,
} from 'api/adminApiClient';
import {
  BulkCustomerImportResponse,
  CreateCustomerRequest,
} from 'api/backofficeApiClient';

import { BulkImportResultModel } from 'components/modals/BulkImportResultModal';
import { ClientBulkImportForm } from '../features/client/ClientBulkImportPage/ClientBulkImportTableSection';
import { findPayCodeById } from './multiRates/multiRatesUtils';

type BulkImportResponse =
  | BulkCustomerImportResponse
  | BulkAssignmentImportResponse;

const mapValuesToBulkImportResult = (
  data: BulkImportResponse[],
): BulkImportResultModel[] => {
  return data.map((data: BulkImportResponse) => ({
    success: data.success,
    validationFailures: data.validationFailures,
    errors: data.errors,
  }));
};

interface Location {
  Address: string;
  City: string;
  StateId: string;
  State: string;
  CountryId: string;
  Country: string;
  CountryCode: string;
  InternationalCityName: string;
  InternationalStateName: string;
  ZipCode: string;
}

interface WorkerFile {
  FileName: string;
  DownloadUrl: string;
  FileKey: string;
}

interface FinancialInfo {
  PayType: string;
  PayRate: string;
  BillRate: string;
  DtPayRate: string;
  DtBillRate: string;
  ProfitRate: string;
  Overtime: string;
  OvertimePayRate: string;
  OvertimeBillRate: string;
  MbpRate: string;
  VmsFee: string;
  MspFee: string;
}

interface AssignmentDictionaryModel {
  AssignmentType: string;
  AssignmentCategory: string;
  CustomerId: string;
  CustomerCompanyName: string;
  AssignmentTeammates: {
    PendingTeammateEmail: string;
    ExternalFullName: string;
    Rate: string;
    RateType: string;
  };
  WorkerInfo: {
    WorkerId: string;
    WorkerInfo: {
      FirstName: string;
      LastName: string;
      Email: string;
      PhoneNumber: string;
      TaxId: string;
      WorkerFileKey: string;
    };
    Location: Location;
    WorkerType: string;
    WorkerFile: WorkerFile;
  };
  ContractorCompanyInfo: {
    CompanyId: string;
    RepresentativeContact: {
      FirstName: string;
      LastName: string;
      Email: string;
      PhoneNumber: string;
      TaxId: string;
      WorkerFileKey: string;
    };
    CompanyName: string;
    Location: Location;
    WorkerFile: WorkerFile;
  };
  AssignmentInfo: {
    JobTitle: string;
    StartDate: string;
    EndDate: string;
    JobDescription: string;
    WorkFromHome: string;
    PreScreeningRequired: string;
    PreScreeningBilledTo: string;
    JobCategory: string;
    EmploymentType: string;
    AnticipatedSchedule: {
      FromTime: string;
      FromAMPM: string;
      ToTime: string;
      ToAMPM: string;
    };
    SpecialInstructions: string;
    CustomerPurchaseOrder: string;
    WorkCode: string;
  };
  FinancialInfo: FinancialInfo;
  BestPrimaryContacts: {
    ContactId: string;
    Title: string;
    FirstName: string;
    LastName: string;
    Email: string;
    PhoneNumber: string;
  };
  BestBillingContacts: {
    ContactId: string;
    Title: string;
    FirstName: string;
    LastName: string;
    Email: string;
    PhoneNumber: string;
  };
  SupervisorInfo: {
    SupervisorId: string;
    Title: string;
    FirstName: string;
    LastName: string;
    Email: string;
    PhoneNumber: string;
  };
  TimesheetApproverNotApplicable: string;
  TimesheetApproverSameAsSupervisor: string;
  TimesheetApproverInfo: {
    TimesheetApproverId: string;
    FirstName: string;
    LastName: string;
    Email: string;
    PhoneNumber: string;
  };
  WorkLocation: Location;
  BillingLocation: Location;
  BillingEmail: string;
  SplitInfo: {
    SplitType: string;
    SplitPercent: string;
    SplitAmount: string;
  };
  PartnerDraft: {
    CompanyName: string;
    FirstName: string;
    LastName: string;
    Email: string;
    PhoneNumber: string;
    SubcontractorRate: string;
    Address: string;
    City: string;
    StateId: string;
    CountryId: string;
    InternationalCityName: string;
    InternationalStateName: string;
    ZipCode: string;
  };
}

export const getAssignmentFieldName = (
  validationFailure: ValidationFailure,
  payCodesDetailsInfo: PayCodeModel[],
  payRateTypes: PayRateTypeModel[],
) => {
  try {
    const { propertyName: fieldName = '' } = validationFailure;

    if ('BestPrimaryContacts' === fieldName) return 'Primary contact email';

    const fieldNameWithoutIndex: string = fieldName.replace(/\[\d+\]/g, '');

    const keys = fieldNameWithoutIndex.split('.');

    if (fieldNameWithoutIndex.startsWith('MultiRates')) {
      const payCodeId = validationFailure.attemptedValue?.payCodeId;
      if (payCodeId && payCodesDetailsInfo && payRateTypes) {
        const payCode = findPayCodeById(
          payCodesDetailsInfo,
          payRateTypes,
          payCodeId,
        );
        const payCodeName = payCode?.name || 'Unknown Pay Code';
        return `${payCodeName} pay code: ${validationFailure.errorMessage}`;
      }
      return 'Pay code error';
    }

    const dictionary: AssignmentDictionaryModel = {
      AssignmentType: 'Assignment type',
      AssignmentCategory: 'Assignment category',
      CustomerId: 'Customer company id',
      CustomerCompanyName: 'Customer company name',
      AssignmentTeammates: {
        PendingTeammateEmail: 'Recruiter email',
        ExternalFullName: 'Recruiter full name',
        Rate: 'Recruiter rate',
        RateType: 'Recruiter rate type',
      },
      WorkerInfo: {
        WorkerId: 'Worker id',
        WorkerInfo: {
          FirstName: 'Worker first name',
          LastName: 'Worker last name',
          Email: 'Worker email',
          PhoneNumber: 'Worker phone number',
          TaxId: 'Worker tax id',
          WorkerFileKey: 'Worker file',
        },
        Location: {
          Address: 'Worker address',
          City: 'Worker city',
          StateId: 'Worker state',
          State: 'Worker state',
          CountryId: 'Worker country',
          Country: 'Worker country',
          CountryCode: 'Worker country',
          InternationalCityName: 'Worker city',
          InternationalStateName: 'Worker state',
          ZipCode: 'Worker ZIP code',
        },
        WorkerType: 'Worker type',
        WorkerFile: {
          FileName: 'Worker file',
          DownloadUrl: 'Worker file',
          FileKey: 'Worker file',
        },
      },
      ContractorCompanyInfo: {
        CompanyId: 'Contractor company company id',
        RepresentativeContact: {
          FirstName: 'Contractor company first name',
          LastName: 'Contractor company last name',
          Email: 'Contractor company email',
          PhoneNumber: 'Contractor company phone number',
          TaxId: 'Contractor company tax id',
          WorkerFileKey: 'Contractor company file',
        },
        CompanyName: 'Contractor company company name',
        Location: {
          Address: 'Contractor company address',
          City: 'Contractor company city',
          StateId: 'Contractor company state',
          State: 'Contractor company state',
          CountryId: 'Contractor company country',
          Country: 'Contractor company country',
          CountryCode: 'Contractor company country',
          InternationalCityName: 'Contractor company city',
          InternationalStateName: 'Contractor company state',
          ZipCode: 'Contractor company ZIP code',
        },
        WorkerFile: {
          FileName: 'Contractor company file',
          DownloadUrl: 'Contractor company file',
          FileKey: 'Contractor company file',
        },
      },
      AssignmentInfo: {
        JobTitle: 'Job title',
        StartDate: 'Start date',
        EndDate: 'End date',
        JobDescription: 'Job description',
        WorkFromHome: 'Working location',
        PreScreeningRequired: 'Pre-screening required',
        PreScreeningBilledTo: 'Pre-screening billed to',
        JobCategory: 'Job category',
        EmploymentType: 'Employment type',
        AnticipatedSchedule: {
          FromTime: 'Anticipated schedule',
          FromAMPM: 'Anticipated schedule',
          ToTime: 'Anticipated schedule',
          ToAMPM: 'Anticipated schedule',
        },
        SpecialInstructions: 'Special instructions',
        CustomerPurchaseOrder: 'Customer PO number',
        WorkCode: 'Work Code',
      },
      FinancialInfo: {
        PayType: 'Pay type',
        PayRate: 'Pay rate',
        BillRate: 'Bill rate',
        DtPayRate: 'DT Pay rate',
        DtBillRate: 'DT Bill rate',
        ProfitRate: 'Profit rate',
        Overtime: 'Overtime',
        OvertimePayRate: 'OT Pay Rate',
        OvertimeBillRate: 'OT Bill Rate',
        MbpRate: 'MBP rate',
        VmsFee: 'VMS rate',
        MspFee: 'MSP rate',
      },
      BestPrimaryContacts: {
        ContactId: 'Primary contact id',
        Title: 'Primary contact title',
        FirstName: 'Primary contact first name',
        LastName: 'Primary contact last name',
        Email: 'Primary contact email',
        PhoneNumber: 'Primary contact phone number',
      },
      BestBillingContacts: {
        ContactId: 'Billing contact id',
        Title: 'Billing contact title',
        FirstName: 'Billing contact first name',
        LastName: 'Billing contact last name',
        Email: 'Billing contact email',
        PhoneNumber: 'Billing contact phone number',
      },
      SupervisorInfo: {
        SupervisorId: 'Supervisor id',
        Title: 'Supervisor title',
        FirstName: 'Supervisor first name',
        LastName: 'Supervisor last name',
        Email: 'Supervisor email',
        PhoneNumber: 'Supervisor phone',
      },
      TimesheetApproverNotApplicable: 'No applicable',
      TimesheetApproverSameAsSupervisor: 'The same as supervisor',
      TimesheetApproverInfo: {
        TimesheetApproverId: 'Ts approver first name',
        FirstName: 'Ts approver first name',
        LastName: 'Ts approver last name',
        Email: 'Ts approver 1 email',
        PhoneNumber: 'Ts approver phone',
      },
      WorkLocation: {
        Address: 'Work address',
        City: 'Work city',
        StateId: 'Work state',
        State: 'Work state',
        CountryId: 'Work country',
        Country: 'Work country',
        CountryCode: 'Work country',
        InternationalCityName: 'Work city',
        InternationalStateName: 'Work state',
        ZipCode: 'Work ZIP code',
      },
      BillingLocation: {
        Address: 'Billing address',
        City: 'Billing city',
        StateId: 'Billing state',
        State: 'Billing state',
        CountryId: 'Billing country',
        Country: 'Billing country',
        CountryCode: 'Billing country',
        InternationalCityName: 'Billing city',
        InternationalStateName: 'Billing state',
        ZipCode: 'Billing ZIP code',
      },
      BillingEmail: 'Billing email',
      SplitInfo: {
        SplitType: 'Split type',
        SplitPercent: 'Split percent',
        SplitAmount: 'Split amount',
      },
      PartnerDraft: {
        CompanyName: 'Split Company name',
        FirstName: 'Split First name',
        LastName: 'Split Last name',
        Email: 'Split Email',
        PhoneNumber: 'Split Phone number',
        SubcontractorRate: 'Split amount',
        Address: 'Split Address',
        City: 'Split City',
        StateId: 'Split State',
        CountryId: 'Split Country',
        InternationalCityName: 'Split City',
        InternationalStateName: 'Split State',
        ZipCode: 'Split ZIP',
      },
    };
    return keys?.reduce((acc: any, curr: string) => acc[curr], dictionary);
  } catch (e) {
    return false;
  }
};

const mapCustomerBulkImportToRequest = (
  values: ClientBulkImportForm,
): CreateCustomerRequest[] => {
  return values.customers.map((customer) => ({
    ...customer,
    agreementDetailsInfo: {
      ...customer.agreementDetailsInfo,
      agreementAttachments:
        customer.agreementDetailsInfo.agreementAttachments?.map((file) => ({
          fileKey: file.s3Key,
          fileName: file.fileName,
        })),
    },
  }));
};

export { mapValuesToBulkImportResult, mapCustomerBulkImportToRequest };
